import type {
  EvidenceRef,
  HistoryItem,
  InvestorObjective,
  Responsiveness,
  StaffCase,
  StaffHistoryItem,
  StaffObjective,
} from '@fruuit/shared'
import { Badge, type BadgeTone, Card, cn, EmptyState, StatusBadge, Timeline } from '@fruuit/ui'
import { EyeOff, FileText, Globe, History } from 'lucide-react'
import type { ReactNode } from 'react'
import { shortDate, stageName } from './format'

/**
 * Staff-only visibility treatment (demo brief): everything on the team case page is marked
 * either "Released to investors" or "Internal – not visible to investors".
 */
export function VisibilityTag({ released }: { released: boolean }) {
  return released ? (
    <Badge tone="positive" icon={<Globe />}>
      Released to investors
    </Badge>
  ) : (
    <Badge tone="watch" icon={<EyeOff />} className="border-dashed">
      Internal – not visible to investors
    </Badge>
  )
}

export type ReleaseStatus = 'released' | 'changed' | 'internal'

/**
 * Where the engagement's investor-visible fields stand: released and current, released but
 * edited since (investors still see the released copy), or never released.
 */
export function releaseStatus(
  data: Pick<StaffCase, 'released' | 'title' | 'theme' | 'status' | 'next_action' | 'due_date'>,
): ReleaseStatus {
  const r = data.released
  if (!r) return 'internal'
  const current =
    r.title === data.title &&
    r.theme === data.theme &&
    r.status === data.status &&
    r.next_action === data.next_action &&
    r.due_date === data.due_date
  return current ? 'released' : 'changed'
}

/** The case header's release tag (UX audit P1-2): never "internal" for a released case. */
export function ReleaseTag({ status }: { status: ReleaseStatus }) {
  if (status === 'changed') {
    return (
      <span title="Investors see the last released copy; the working fields differ.">
        <Badge tone="watch" icon={<Globe />}>
          Changes not yet released
        </Badge>
      </span>
    )
  }
  return <VisibilityTag released={status === 'released'} />
}

/** A dashed amber frame for internal content on the staff case page. */
export function InternalFrame({
  internal,
  children,
  className,
}: {
  internal: boolean
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'rounded-md',
        internal && 'border border-dashed border-watch-line bg-watch-soft/40 p-3',
        className,
      )}
    >
      {children}
    </div>
  )
}

/** Example.invalid and non-http links stay plain text (INV-010). */
function EvidenceLink({ source }: { source: EvidenceRef }) {
  const linkable = /^https?:\/\//.test(source.url) && !source.url.includes('example.invalid')
  const label = (
    <>
      <span className="font-medium text-ink">{source.title}</span>
      <span className="text-sage">
        {' '}
        · {source.source_class}
        {source.publication_date ? ` · ${shortDate(source.publication_date)}` : ''}
      </span>
    </>
  )
  return (
    <li className="flex items-start gap-1.5 text-xs leading-relaxed">
      <FileText aria-hidden="true" className="mt-0.5 size-3.5 shrink-0 text-teal-500" />
      {linkable ? (
        <a href={source.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
          {label}
        </a>
      ) : (
        <span>{label}</span>
      )}
    </li>
  )
}

type AnyObjective = InvestorObjective | StaffObjective

/**
 * Progress against an objective, with evidence: each milestone with what changed since the
 * previous update, the evidence it cites and what remains outstanding. Investors see
 * published milestones only; staff see every state, marked internal or released.
 */
export function MilestoneProgress({ objective }: { objective: AnyObjective }) {
  const staff = 'state' in objective
  if (objective.milestones.length === 0) {
    return (
      <p className="mt-4 text-xs text-sage">
        No evidence reviews released yet. The stage stays at Baseline until one is released.
      </p>
    )
  }
  return (
    <div className="mt-5">
      <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-sage">
        Progress record ({objective.milestones.length})
      </h4>
      <ol className="mt-2 space-y-2">
        {objective.milestones.map((m) => {
          const state = 'state' in m ? m.state : 'published'
          const released = state === 'published'
          return (
            <li key={m.id}>
              <InternalFrame
                internal={staff && !released}
                className={cn(!(staff && !released) && 'border border-line-soft p-3')}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-ink">
                    {stageName(m.stage)}
                    <span className="font-normal text-sage">
                      {' '}
                      · assessed {shortDate(m.assessment_date)}
                    </span>
                  </p>
                  {staff ? (
                    <span className="flex flex-wrap gap-1.5">
                      {!released ? <StatusBadge status={state} /> : null}
                      <VisibilityTag released={released} />
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 text-xs text-teal-800">
                  <span className="font-semibold">What changed:</span> {m.what_changed}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-ink-soft">{m.rationale}</p>
                <p className="mt-1 text-xs leading-relaxed text-ink-soft">
                  <span className="font-semibold text-ink">Still outstanding:</span> {m.limitations}
                </p>
                {m.evidence.length > 0 ? (
                  <ul className="mt-2 space-y-1" aria-label="Evidence">
                    {m.evidence.map((source) => (
                      <EvidenceLink key={source.id} source={source} />
                    ))}
                  </ul>
                ) : null}
                {m.withheld_evidence_count > 0 ? (
                  <p className="mt-1 text-xs text-sage">
                    {m.withheld_evidence_count === 1
                      ? '1 further evidence reference is internal to Fruuit.'
                      : `${m.withheld_evidence_count} further evidence references are internal to Fruuit.`}
                  </p>
                ) : null}
              </InternalFrame>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

const RESPONSIVENESS_TONE: Record<Responsiveness['label'], BadgeTone> = {
  responsive: 'positive',
  slow: 'watch',
  unresponsive: 'attention',
  not_assessed: 'quiet',
}

/** Company responsiveness, derived on the server; the rule is in the tooltip and below. */
export function ResponsivenessCard({ value }: { value: Responsiveness }) {
  return (
    <Card title="Company responsiveness" padding="md">
      <div className="flex flex-wrap items-center gap-2" title={value.rule}>
        <Badge tone={RESPONSIVENESS_TONE[value.label]} dot>
          {value.label_text}
        </Badge>
        <span className="text-xs text-sage">as of {shortDate(value.evaluated_at)}</span>
      </div>
      <dl className="mt-4 grid grid-cols-3 gap-3 text-center">
        <div className="rounded-md bg-wash px-2 py-2.5">
          <dt className="text-[11px] leading-tight text-sage">Last company response</dt>
          <dd className="mt-1 text-sm font-semibold text-ink">
            {shortDate(value.last_response_date, 'None yet')}
          </dd>
        </div>
        <div className="rounded-md bg-wash px-2 py-2.5">
          <dt className="text-[11px] leading-tight text-sage">Days awaiting a response</dt>
          <dd className="mt-1 text-sm font-semibold text-ink">{value.days_awaiting ?? '–'}</dd>
        </div>
        <div className="rounded-md bg-wash px-2 py-2.5">
          <dt className="text-[11px] leading-tight text-sage">Unanswered follow-ups</dt>
          <dd className="mt-1 text-sm font-semibold text-ink">{value.unanswered}</dd>
        </div>
      </dl>
      <details className="mt-3 text-xs text-sage">
        <summary className="cursor-pointer font-medium text-teal hover:underline">
          How this is measured
        </summary>
        <p className="mt-2 leading-relaxed">{value.rule}</p>
      </details>
    </Card>
  )
}

const KIND_LABEL: Record<HistoryItem['kind'], string> = {
  milestone: 'Evidence review',
  dialogue: 'Correspondence',
  meeting: 'Meeting',
  assessment: 'Assessment',
}

/**
 * History titles are "<type>: <title>"; a meeting titled "Investor briefing: …" of type
 * "Investor briefing" would read the prefix twice, so collapse a repeated prefix.
 */
export function historyTitle(title: string): string {
  return title.replace(/^([^:]+): \1: /i, '$1: ')
}

/**
 * One chronological record of the case (newest first): milestones, correspondence, meetings
 * and assessment versions. Staff items carry the released/internal marking.
 */
export function CaseHistory({ items }: { items: (HistoryItem | StaffHistoryItem)[] }) {
  return (
    <Card eyebrow="Case history" title="Everything that happened, in order">
      {items.length === 0 ? (
        <EmptyState
          variant="plain"
          icon={<History />}
          title="Nothing recorded yet."
          className="py-8"
        />
      ) : (
        <Timeline
          label="Case history"
          items={items.map((item) => {
            const internal = 'visibility' in item && item.visibility === 'internal'
            return {
              id: `${item.kind}-${item.id}`,
              date: shortDate(item.date),
              dateNote: KIND_LABEL[item.kind],
              tone: internal ? 'watch' : item.kind === 'milestone' ? 'positive' : 'info',
              title: (
                <span className="inline-flex flex-wrap items-center gap-2">
                  {historyTitle(item.title)}
                  {'visibility' in item ? (
                    <VisibilityTag released={item.visibility === 'released'} />
                  ) : null}
                </span>
              ),
              body: item.detail ?? undefined,
            }
          })}
        />
      )}
    </Card>
  )
}
